'use client';

import type { LDContext, LDFlagChangeset, LDFlagSet } from 'launchdarkly-js-client-sdk';
import React, { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { initLDClient } from './client';

export { FLAGS } from './flags';

if (!process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID) {
  throw new Error('NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID is not set');
}

type LaunchDarklyContextType = {
  flags: LDFlagSet | null;
  isReady: boolean;
};

const LaunchDarklyContext = createContext<LaunchDarklyContextType>({
  flags: null,
  isReady: false,
});

export const useFlag = (flag: string) => {
  const { flags } = useContext(LaunchDarklyContext);

  return flags?.[flag] || false;
};

export const useLaunchDarkly = () => useContext(LaunchDarklyContext);

export const LaunchDarklyProvider = ({
  children,
  context,
}: {
  children: ReactNode;
  context: LDContext;
}) => {
  const [flags, setFlags] = useState<LDFlagSet | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const client = initLDClient(context, process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID!);

    const handleReady = () => {
      setFlags(client.allFlags());
      setIsReady(true);
      client.identify(context);
    };

    const handleChange = (changes: LDFlagChangeset) => {
      setFlags((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.entries(changes).map(([k, v]) => [k, v.current])),
      }));
    };

    client.on('ready', handleReady);
    client.on('change', handleChange);

    return () => {
      client.off('ready', handleReady);
      client.off('change', handleChange);
      client?.close?.();
    };
  }, [context]);

  return (
    <LaunchDarklyContext.Provider value={{ flags, isReady }}>
      {children}
    </LaunchDarklyContext.Provider>
  );
};
